import { ReactElement } from 'react';

import { InfoOutlineIcon, RecurringSparkIcon } from '@componentLibrary/Icon';
import { Tooltip } from '@componentLibrary/Tooltip';
import Typography from '@componentLibrary/Typography';
import { ESparkStatusColors } from '@constants/SparkConstants';
import { Emoji } from 'emoji-mart';
import moment from 'moment-timezone';

import {
    DetailedSparkType,
    Spark,
    getDetailedSparkLabel,
    isEligibleManagerForGoalSpark,
} from '@sparkplug/lib';

import { EntityDetailsCard } from '@components/EntityDetails/EntityDetailsCard';
import PanelField from '@components/layout/PanelField';

import { useApp } from '@hooks/AppHooks';
import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks/SparkplugAccountsHooks';

import {
    formatSparkInfo,
    getMinThresholdLabel,
    getOtherGroupName,
    getSelectedLocationsFromSparkSubGroups,
    getSelectedParticipantsFromSparkSubGroups,
    getSparkTypeDisplayName,
} from '@helpers/sparks';
import { formatRecurringTooltip } from '@helpers/ui';

import { BadgeColor } from '@app/componentLibrary/Badge';
import { ISparkPosData, ISparkSubGroup } from '@app/types/SparksTypes';

import SparkStatusChip from '../SparkStatusChip';

interface SharedSparkDetailsPanelProps {
    spark: Spark;
    detailedSparkType?: DetailedSparkType;
    onRetailersClick?: () => void;
    onLocationsClick?: () => void;
    onProductClick?: () => void;
    onParticipantsClick?: () => void;
    moreButton?: ReactElement;
}
interface SparkDetailsPanelProps extends SharedSparkDetailsPanelProps {
    otherGroupName?: string;
    vendorGroupName?: string;
    locationCount?: number;
    participantCount?: number;
    productCount?: number;
    retailerCount?: number;
    showBundleData?: boolean;
    sparkPosDataIsReady?: boolean;
    accountType?: 'retailer' | 'brand';
    appIsMobile?: boolean;
}

/**
 * This is a controlled version of the SparkDetailsPanel component where we can manually
 * pass in the location, participant, and product counts as well as the other group name
 * and the vendor name.
 */
export const SparkDetailsPanel = ({
    spark,
    detailedSparkType,
    otherGroupName,
    vendorGroupName,
    locationCount = 0,
    participantCount = 0,
    productCount = 0,
    retailerCount = 0,
    showBundleData = false,
    onRetailersClick,
    onLocationsClick,
    onProductClick,
    onParticipantsClick,
    moreButton,
    sparkPosDataIsReady = false,
    accountType,
    appIsMobile = false,
}: SparkDetailsPanelProps) => {
    const {
        name,
        description,
        type: sparkType,
        emoji,
        startDate,
        endDate,
        recurringSchedule,
        sparkBrand,
        paymentMethod,
        reminderSentAt,
        createdAt,
    } = spark;

    const requestState = spark.bundle?.requestState ?? spark.requestState;

    const {
        formattedStartDate,
        formattedEndDate,
        totalDays: duration,
        status,
    } = formatSparkInfo(startDate, endDate, requestState, recurringSchedule);

    const participantType = detailedSparkType === 'goalManager' ? 'Managers' : 'Employees';
    const minThresholdLabel = getMinThresholdLabel(spark);

    const isManagerGoal = detailedSparkType === 'goalManager';
    const isPaymentMethodSpark = paymentMethod;

    let statusChipTooltip = '';
    if (requestState === 'pending') {
        if (spark.reminderSentAt) {
            statusChipTooltip = `Pending Spark reminder sent on ${moment(reminderSentAt).format(
                'LL',
            )}`;
        } else {
            statusChipTooltip = `Spark sent on ${moment(createdAt).format('LL')}`;
        }
    }

    const sparkTypeDisplayName = getSparkTypeDisplayName(sparkType);
    const statusColor = ESparkStatusColors[status];
    let statusLabel = 'Complete';
    if (requestState?.toLowerCase() === 'rejected') {
        statusLabel = 'Declined';
    } else if (status.toLowerCase() !== 'complete') {
        statusLabel = status;
    }

    let productText = '--';
    if (showBundleData || sparkPosDataIsReady) {
        productText = productCount > 0 ? `${productCount} Products` : '0 Products';
    }

    const metaDataGroups = [
        {
            metadata: [
                {
                    label: 'Start Date',
                    value: formattedStartDate,
                },
                {
                    label: 'End Date',
                    value: formattedEndDate,
                },
                {
                    label: 'Duration',
                    value: `${duration} days`,
                },
                {
                    label:
                        minThresholdLabel && sparkType === 'commission' ? 'Minimum Payout' : 'Type',
                    value:
                        minThresholdLabel && sparkType === 'commission'
                            ? minThresholdLabel
                            : getDetailedSparkLabel(detailedSparkType as DetailedSparkType, false),
                },
            ],
        },
        {
            metadata: [
                ...(showBundleData
                    ? [
                          {
                              label: 'Participating Retailers',
                              value: (
                                  <PanelField
                                      value={
                                          retailerCount > 0 ? `${retailerCount} Retailers` : '--'
                                      }
                                      onClick={retailerCount ? onRetailersClick : null}
                                  />
                              ),
                          },
                      ]
                    : []),
                {
                    label: 'Participating Locations',
                    value: (
                        <PanelField
                            value={locationCount > 0 ? `${locationCount} Locations` : '--'}
                            onClick={locationCount ? onLocationsClick : null}
                        />
                    ),
                },
                {
                    label: 'Qualifying Products',
                    value: (
                        <div className="relative">
                            <PanelField
                                value={productText}
                                onClick={productCount ? onProductClick : null}
                            />
                            {spark.omitPreorders && (
                                <Typography
                                    as="span"
                                    variant="xs"
                                    className="absolute top-full left-0 flex items-center gap-1 leading-none whitespace-nowrap"
                                >
                                    <em>In-store only</em>
                                    <Tooltip
                                        title="Only in-store sales count toward this Spark. Preorders are not included."
                                        placement="top"
                                    >
                                        <InfoOutlineIcon className="w-3 h-3" />
                                    </Tooltip>
                                </Typography>
                            )}
                        </div>
                    ),
                },
                {
                    label: `Participating ${participantType}`,
                    value: (
                        <PanelField
                            value={
                                participantCount > 0
                                    ? `${participantCount} ${participantType}`
                                    : '--'
                            }
                            onClick={participantCount ? onParticipantsClick : null}
                        />
                    ),
                },
            ],
        },
    ];

    const sparkImage = spark.sparkBrand ? (
        <div className="relative mr-5">
            <img
                className="w-16 h-16 mr-3 rounded"
                src={sparkBrand?.photo}
                alt={sparkBrand?.name}
            />
            {!!recurringSchedule && (
                <Tooltip title={formatRecurringTooltip(recurringSchedule)}>
                    <span className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 rounded-full p-1 bg-white flex items-center justify-center shadow-sm">
                        <RecurringSparkIcon className="w-6 h-6 text-[#62676E]" />
                    </span>
                </Tooltip>
            )}
        </div>
    ) : (
        <div className="emoji-wrapper relative mr-5">
            <Emoji emoji={emoji} set="apple" size={40} />
            {!!recurringSchedule && (
                <Tooltip title={formatRecurringTooltip(recurringSchedule)}>
                    <span
                        id="recurring-icon-container"
                        className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 rounded-full p-1 bg-white flex items-center justify-center shadow-sm"
                    >
                        <RecurringSparkIcon className="w-6 h-6 text-[#62676E]" />
                    </span>
                </Tooltip>
            )}
        </div>
    );

    const getSecondaryBadge = () => {
        if (isManagerGoal) return { label: 'M', color: 'yellow' as BadgeColor };
        if (isPaymentMethodSpark) return { label: '$', color: 'green' as BadgeColor };
        return undefined;
    };

    return (
        <EntityDetailsCard
            appIsMobile={appIsMobile}
            className="[&_.panel-field]:!p-0 [&_.panel-field]:!mt-[2px] [&_p]:!mb-0 mb-2"
            title={sparkTypeDisplayName}
            titleBadge={{
                label: otherGroupName ?? '',
                color: 'cerulean',
                tooltip: accountType === 'retailer' || showBundleData ? vendorGroupName : '',
            }}
            secondaryTitleBadge={getSecondaryBadge()}
            name={name}
            image={sparkImage}
            description={description}
            customBadge={<SparkStatusChip status={status} customLabel={statusLabel} size="lg" />}
            metadataGroups={metaDataGroups}
            actions={<div className="[&_.icon-btn]:mr-[-16px] text-gray-300"> {moreButton}</div>}
        />
    );
};

/**
 * This is a wrapped version of the SparkDetailsPanel for when we want to use the
 * sparkPosData and sparkSubGroups to calculate the location, participant, and product counts
 * as well as the other group name and vendor group name.
 *
 * Bundle aggregate data is resolved here rather than in the presentational component,
 * and is only shown when viewing from a vendor (brand) perspective.
 */
export default ({
    spark,
    sparkPosData,
    sparkPosDataIsReady,
    sparkSubGroups,
    detailedSparkType,
    ...sharedProps
}: SharedSparkDetailsPanelProps & {
    sparkPosData: ISparkPosData;
    sparkPosDataIsReady: boolean;
    sparkSubGroups: ISparkSubGroup[];
}) => {
    const { account } = useSparkplugAccount();
    const { appIsMobile } = useApp();

    const { associatedProducts, locations, participants } = sparkPosData;

    const otherGroupName = getOtherGroupName(spark, account);
    const vendorGroupName = spark.sparkVendor?.name ?? '';
    const subGroupsLocations = getSelectedLocationsFromSparkSubGroups(sparkSubGroups);
    const subGroupsParticipants = getSelectedParticipantsFromSparkSubGroups(sparkSubGroups);

    // Bundle data is only shown from the vendor (brand) perspective
    const isVendorView = account?.type === 'brand';
    const showBundleData = isVendorView && !!spark.bundle;
    const bundleCounts = isVendorView ? spark.effectiveBundleCounts : undefined;

    // Calculate base counts from POS data
    const baseProductCount = associatedProducts?.length ?? 0;
    const baseLocationCount =
        detailedSparkType === 'leaderboardMulti'
            ? subGroupsLocations.length
            : locations?.length ?? 0;

    const getBaseParticipantCount = () => {
        if (detailedSparkType === 'leaderboardMulti') {
            return subGroupsParticipants.length;
        }
        if (detailedSparkType === 'goalManager') {
            return (
                participants?.filter((p) => isEligibleManagerForGoalSpark(p, spark.locationIds))
                    .length ?? 0
            );
        }
        return participants?.length ?? 0;
    };
    const baseParticipantCount = getBaseParticipantCount();

    const locationCount = bundleCounts?.locationCount ?? baseLocationCount;
    const participantCount = bundleCounts?.participantCount ?? baseParticipantCount;
    const productCount = bundleCounts?.productCount ?? baseProductCount;
    const retailerCount = bundleCounts?.retailerCount ?? 0;

    return (
        <SparkDetailsPanel
            spark={spark}
            detailedSparkType={detailedSparkType}
            otherGroupName={otherGroupName}
            vendorGroupName={vendorGroupName}
            locationCount={locationCount}
            participantCount={participantCount}
            productCount={productCount}
            retailerCount={retailerCount}
            showBundleData={showBundleData}
            sparkPosDataIsReady={sparkPosDataIsReady}
            {...sharedProps}
            appIsMobile={appIsMobile}
            accountType={account?.type}
        />
    );
};
